// frontend/src/composables/useEmployeeValidation.js - Employee Form Validation
import { ref, reactive, computed } from 'vue';

export function useEmployeeValidation() {
  const errors = reactive({});
  const isSubmitting = ref(false);

  // Validation rules - Combined from both versions for complete compatibility
  const rules = {
    employee_id: {
      required: true,
      minLength: 3,
      pattern: /^[A-Z0-9]{3,10}$/i,
      message: 'Employee ID must be 3-10 characters (letters and numbers only)',
    },
    name: {
      required: true,
      minLength: 2,
      maxLength: 100,
      pattern: /^[a-zA-Z\s'-]+$/,
      message:
        'Name must be 2-100 characters (letters, spaces, hyphens, apostrophes only)',
    },
    email: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: 'Please enter a valid email address',
    },
    phone: {
      required: false,
      pattern: /^[\+]?[0-9\s\-\(\)]{10,15}$/,
      message: 'Please enter a valid phone number (10-15 digits)',
    },
    department: {
      required: true,
      message: 'Please select a department',
    },
    position: {
      required: true,
      minLength: 2,
      maxLength: 100,
      message: 'Position must be 2-100 characters',
    },
    salary: {
      required: false,
      min: 0,
      max: 10000000,
      message: 'Salary must be between 0 and 10,000,000',
    },
    hire_date: {
      required: false, // Made optional to match the form
      message: 'Please select a hire date',
    },
    password: {
      required: true,
      minLength: 6, // Simplified for easier use
      message: 'Password must be at least 6 characters long',
    },
    confirmPassword: {
      required: true,
      message: 'Please confirm your password',
    },
  };

  // Get user-friendly field labels
  const getFieldLabel = field => {
    const labels = {
      employee_id: 'Employee ID',
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      department: 'Department',
      position: 'Position',
      salary: 'Salary',
      hire_date: 'Hire Date',
      work_schedule: 'Work Schedule',
      password: 'Password',
      confirmPassword: 'Confirm Password',
    };
    return labels[field] || field.charAt(0).toUpperCase() + field.slice(1);
  };

  // Validate individual field
  const validateField = (field, value, additionalContext = {}) => {
    const rule = rules[field];
    if (!rule) return true;

    // Clear existing error
    delete errors[field];

    // Required validation
    if (rule.required && (!value || value.toString().trim() === '')) {
      errors[field] = `${getFieldLabel(field)} is required`;
      return false;
    }

    // Skip other validations if field is not required and empty
    if (!rule.required && (!value || value.toString().trim() === '')) {
      return true;
    }

    const stringValue = value.toString().trim();

    // Length validations
    if (rule.minLength && stringValue.length < rule.minLength) {
      errors[field] = `${getFieldLabel(field)} must be at least ${
        rule.minLength
      } characters`;
      return false;
    }

    if (rule.maxLength && stringValue.length > rule.maxLength) {
      errors[field] = `${getFieldLabel(field)} must not exceed ${
        rule.maxLength
      } characters`;
      return false;
    }

    // Numeric validations
    if (rule.min !== undefined) {
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < rule.min) {
        errors[field] = `${getFieldLabel(field)} must be at least ${rule.min}`;
        return false;
      }
    }

    if (rule.max !== undefined) {
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue > rule.max) {
        errors[field] = `${getFieldLabel(field)} must not exceed ${rule.max}`;
        return false;
      }
    }

    // Pattern validations
    if (rule.pattern && !rule.pattern.test(stringValue)) {
      errors[field] = rule.message || `Invalid ${getFieldLabel(field)} format`;
      return false;
    }

    // Special validations
    if (field === 'confirmPassword') {
      if (value !== additionalContext.password) {
        errors[field] = 'Passwords do not match';
        return false;
      }
    }

    if (field === 'hire_date' && value) {
      const hireDate = new Date(value);
      const today = new Date();
      const maxPastDate = new Date();
      maxPastDate.setFullYear(today.getFullYear() - 50); // 50 years ago

      if (hireDate > today) {
        errors[field] = 'Hire date cannot be in the future';
        return false;
      }

      if (hireDate < maxPastDate) {
        errors[field] = 'Hire date cannot be more than 50 years ago';
        return false;
      }
    }

    return true;
  };

  // Validate entire form
  const validateForm = (formData, isEdit = false) => {
    // Clear all errors
    Object.keys(errors).forEach(key => delete errors[key]);

    let isValid = true;

    // Required fields - matching the Employee component structure
    const fieldsToValidate = ['name', 'email', 'department', 'position'];

    // Add optional fields that might be present
    if (formData.phone) fieldsToValidate.push('phone');
    if (formData.employee_id) fieldsToValidate.push('employee_id');
    if (formData.salary) fieldsToValidate.push('salary');
    if (formData.hire_date) fieldsToValidate.push('hire_date');

    // Add password validation for new employees
    if (!isEdit && formData.password) {
      fieldsToValidate.push('password', 'confirmPassword');
    }

    // Validate each field
    for (const field of fieldsToValidate) {
      const fieldValid = validateField(field, formData[field], {
        password: formData.password,
      });
      if (!fieldValid) {
        isValid = false;
      }
    }

    return isValid;
  };

  // Validate field on input (for real-time validation)
  const validateOnInput = (field, value, additionalContext = {}) => {
    validateField(field, value, additionalContext);
  };

  // Check if field has error
  const hasError = field => {
    return !!errors[field];
  };

  // Get error message for field
  const getError = field => {
    return errors[field] || '';
  };

  // Check if form has any errors
  const hasErrors = computed(() => {
    return Object.keys(errors).length > 0;
  });

  // Clear all errors
  const clearErrors = () => {
    Object.keys(errors).forEach(key => delete errors[key]);
  };

  // Clear specific field error
  const clearError = field => {
    delete errors[field];
  };

  // Set error manually
  const setError = (field, message) => {
    errors[field] = message;
  };

  // Set submitting state
  const setSubmitting = submitting => {
    isSubmitting.value = submitting;
  };

  // Validate email uniqueness (would need to call API)
  const validateEmailUnique = async (email, excludeId = null) => {
    try {
      // This would call an API endpoint to check email uniqueness
      // For now, just return true
      return true;
    } catch (error) {
      console.error('Error validating email uniqueness:', error);
      return true;
    }
  };

  // Validate employee ID uniqueness
  const validateEmployeeIdUnique = async (employeeId, excludeId = null) => {
    try {
      // This would call an API endpoint to check employee ID uniqueness
      // For now, just return true
      return true;
    } catch (error) {
      console.error('Error validating employee ID uniqueness:', error);
      return true;
    }
  };

  // Generate employee ID suggestion
  const generateEmployeeId = (name, department) => {
    if (!name || !department) return '';

    const nameInitials = name
      .split(' ')
      .map(part => part.charAt(0).toUpperCase())
      .join('');
    const deptCode = department.substring(0, 3).toUpperCase();
    const timestamp = Date.now().toString().slice(-4);

    return `${deptCode}${nameInitials}${timestamp}`;
  };

  // Format phone number as user types
  const formatPhoneNumber = phone => {
    if (!phone) return '';

    // Remove all non-numeric characters
    const numbers = phone.replace(/\D/g, '');

    // Format based on length
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    } else if (numbers.length <= 10) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(
        6
      )}`;
    } else {
      return `+${numbers.slice(0, -10)} (${numbers.slice(
        -10,
        -7
      )}) ${numbers.slice(-7, -4)}-${numbers.slice(-4)}`;
    }
  };

  // Sanitize form data before submission - Compatible with Employee component
  const sanitizeFormData = formData => {
    const sanitized = { ...formData };

    // Trim string fields
    const stringFields = [
      'name',
      'email',
      'phone',
      'department',
      'position',
      'employee_id',
    ];
    stringFields.forEach(field => {
      if (sanitized[field]) {
        sanitized[field] = sanitized[field].toString().trim();
      }
    });

    // Convert salary to number if provided
    if (sanitized.salary && sanitized.salary !== '') {
      sanitized.salary = parseFloat(sanitized.salary);
      if (isNaN(sanitized.salary)) {
        delete sanitized.salary;
      }
    } else {
      delete sanitized.salary;
    }

    // Format employee ID to uppercase
    if (sanitized.employee_id) {
      sanitized.employee_id = sanitized.employee_id.toString().toUpperCase();
    }

    // Handle boolean fields for Employee component compatibility
    if (typeof sanitized.can_work_field !== 'boolean') {
      sanitized.can_work_field = Boolean(sanitized.can_work_field);
    }

    if (typeof sanitized.requires_field_approval !== 'boolean') {
      sanitized.requires_field_approval = Boolean(
        sanitized.requires_field_approval
      );
    }

    // Remove empty optional fields
    if (!sanitized.phone || sanitized.phone === '') {
      delete sanitized.phone;
    }

    // Format phone number (remove spaces, dashes, parentheses)
    if (sanitized.phone) {
      sanitized.phone = sanitized.phone.replace(/[\s\-\(\)]/g, '');
    }

    // Remove confirmPassword field
    delete sanitized.confirmPassword;

    return sanitized;
  };

  return {
    errors,
    isSubmitting,
    validateForm,
    validateOnInput,
    validateField,
    hasError,
    getError,
    hasErrors,
    clearErrors,
    clearError,
    setError,
    setSubmitting,
    validateEmailUnique,
    validateEmployeeIdUnique,
    generateEmployeeId,
    formatPhoneNumber,
    sanitizeFormData,
    getFieldLabel,
  };
}
